import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  requestOtp,
  type RequestOtpResult,
} from '../../../shared/api/auth.service';
import { RpcError } from '../../../shared/api/rpc-client';
import {
  resolveApiLocale,
  resolveAppLanguage,
  type CountryCode,
} from '../../../shared/config';
import { useEmployeeSession } from '../../../shared/hooks/use-employee-session';
import {
  COUNTRY_OPTIONS,
  DEFAULT_COUNTRY_CODE,
  formatPhoneByMask,
  isPhoneLengthComplete,
  normalizePhoneInput,
  resolveCountryOption,
  sanitizePhoneDigits,
  type CountryOption,
} from '../../../shared/model';

const LOGIN_CONSENT_IDS = [
  'personalData',
  'informativeMessages',
  'promotionalMessages',
] as const;

export type LoginConsentId = (typeof LOGIN_CONSENT_IDS)[number];

export type LoginPhoneForm = {
  canSubmit: boolean;
  clearPhone: () => void;
  countryCode: CountryCode;
  countryFlag: string;
  countryOptions: ReadonlyArray<CountryOption>;
  isPhoneComplete: boolean;
  phone: string;
  phoneMask: string;
  selectCountryCode: (country: CountryCode) => void;
  setPhone: (value: string) => void;
};

export type LoginConsentForm = {
  setValue: (id: LoginConsentId, next: boolean) => void;
  values: Record<LoginConsentId, boolean>;
};

type LoginForm = {
  consents: LoginConsentForm;
  phoneForm: LoginPhoneForm;
  isSubmitting: boolean;
  submitError: string | null;
  submit: () => Promise<RequestOtpResult | null>;
};

const DEFAULT_LOGIN_CONSENT_VALUES: Record<LoginConsentId, boolean> = {
  informativeMessages: true,
  personalData: true,
  promotionalMessages: true,
};

function resolveSubmitError(
  error: unknown,
  t: (key: string) => string,
): string | null {
  if (error instanceof RpcError) {
    const msg = error.message.toLowerCase();
    if (
      msg.includes('phone number normalization') ||
      msg.includes('phone number and region mismatch')
    ) {
      return t('login.invalidPhone');
    }
    if (
      msg.includes('too many requests') ||
      msg.includes('temporarily locked')
    ) {
      return t('login.tooManyRequests');
    }
  }
  return t('login.networkError');
}

export function useLoginForm(): LoginForm {
  const { t, i18n } = useTranslation();
  const { authContact, setAuthContact } = useEmployeeSession();
  const initialCountryCode = authContact?.countryCode ?? DEFAULT_COUNTRY_CODE;
  const initialPhoneDigits = authContact?.phoneDigits ?? '';
  const [countryCode, setCountryCode] = useState(() => initialCountryCode);
  const [phone, setPhoneState] = useState(() =>
    formatPhoneByMask(initialPhoneDigits, initialCountryCode),
  );
  const [consentValues, setConsentValues] = useState<
    Record<LoginConsentId, boolean>
  >(() => ({ ...DEFAULT_LOGIN_CONSENT_VALUES }));
  const requestOtpMutation = useMutation({
    mutationFn: ({ locale, phone }: { locale: string; phone: string }) =>
      requestOtp(phone, locale),
  });
  const phoneDigits = sanitizePhoneDigits(phone);
  const selectedCountry = resolveCountryOption(countryCode);
  const isPhoneComplete = isPhoneLengthComplete(phoneDigits.length);

  const canSubmit =
    isPhoneComplete &&
    LOGIN_CONSENT_IDS.every(consentId => consentValues[consentId]);
  const submitError = requestOtpMutation.isError
    ? resolveSubmitError(requestOtpMutation.error, t)
    : null;

  const resetSubmitError = () => {
    if (requestOtpMutation.isError) {
      requestOtpMutation.reset();
    }
  };

  const syncAuthContact = (
    nextCountryCode: CountryCode,
    nextPhoneDigits: string,
  ) => {
    setAuthContact({
      countryCode: nextCountryCode,
      phoneDigits: nextPhoneDigits,
    });
  };

  const selectCountryCode = (nextCountryCode: CountryCode) => {
    const nextPhone = formatPhoneByMask(phoneDigits, nextCountryCode);

    resetSubmitError();
    setCountryCode(nextCountryCode);
    setPhoneState(nextPhone);
    syncAuthContact(nextCountryCode, phoneDigits);
  };

  const setPhone = (value: string) => {
    const { countryCode: nextCountryCode, phoneDigits: nextPhoneDigits } =
      normalizePhoneInput(value, countryCode);

    resetSubmitError();
    setCountryCode(nextCountryCode);
    setPhoneState(formatPhoneByMask(nextPhoneDigits, nextCountryCode));
    syncAuthContact(nextCountryCode, nextPhoneDigits);
  };

  const clearPhone = () => {
    resetSubmitError();
    setPhoneState('');
    syncAuthContact(countryCode, '');
  };

  const setConsentValue = (id: LoginConsentId, next: boolean) => {
    resetSubmitError();
    setConsentValues(current => {
      if (current[id] === next) {
        return current;
      }

      return {
        ...current,
        [id]: next,
      };
    });
  };

  const submit = async (): Promise<RequestOtpResult | null> => {
    if (!isPhoneComplete || requestOtpMutation.isPending) {
      return null;
    }

    syncAuthContact(countryCode, phoneDigits);

    const phoneValue = `${countryCode}${phoneDigits}`;
    const locale = resolveApiLocale(resolveAppLanguage(i18n.language));

    try {
      return await requestOtpMutation.mutateAsync({
        locale,
        phone: phoneValue,
      });
    } catch {
      return null;
    }
  };

  return {
    consents: {
      setValue: setConsentValue,
      values: consentValues,
    },
    phoneForm: {
      canSubmit,
      clearPhone,
      countryCode,
      countryFlag: selectedCountry.flag,
      countryOptions: COUNTRY_OPTIONS,
      isPhoneComplete,
      phone,
      phoneMask: selectedCountry.mask,
      selectCountryCode,
      setPhone,
    },
    isSubmitting: requestOtpMutation.isPending,
    submit,
    submitError,
  };
}
